import { Fuel, Sparkles, Target } from "lucide-react";
import { Badge } from "@/components/ui/badge";

/**
 * BankFuelMeter — compact daily+monthly fuel card for a bank-staff user.
 * Reuses data already fetched by the caller — NO extra API calls.
 *
 * Props:
 *  - takenToday:   number of leads the staff took today
 *  - okThisMonth:  number of OK cases in the current month
 *  - dailyTarget:  daily take target (default 5)
 *  - monthlyTarget: monthly OK target (default 25)
 *  - score:        integer 0-100 (from /bank/wanted)
 *  - vip:          boolean
 */
export default function BankFuelMeter({ takenToday = 0, okThisMonth = 0,
                                        dailyTarget = 5, monthlyTarget = 25,
                                        score = 100, vip = false }) {
  const dailyPct = Math.min(100, Math.round((takenToday / dailyTarget) * 100));
  const monthlyPct = Math.min(100, Math.round((okThisMonth / monthlyTarget) * 100));

  return (
    <div className="rounded-2xl bg-gradient-to-br from-[#141A2E] to-[#0F1424] border border-[#1E2742] p-4 mb-3" data-testid="bank-fuel-meter">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-lg bg-[#FF8C00]/15 border border-[#FF8C00]/40 flex items-center justify-center">
          <Fuel className="w-4 h-4 text-[#FF8C00]"/>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-bold text-[#FF8C00] uppercase tracking-widest">My Fuel Meter</div>
          <div className="text-[11px] text-slate-500">Take {dailyTarget} · Close {monthlyTarget}</div>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          {vip && (
            <Badge className="bg-[#C5A028]/20 text-[#C5A028] border-[#C5A028]/40 text-[9px]" data-testid="fuel-vip-badge">
              <Sparkles className="w-2.5 h-2.5 mr-1"/> VIP
            </Badge>
          )}
          <div className="text-right leading-none">
            <div className="text-[9px] uppercase tracking-widest text-slate-500">Score</div>
            <div className={`font-mono-alt font-bold text-lg leading-none mt-0.5 ${score >= 90 ? "text-emerald-400" : score >= 70 ? "text-[#C5A028]" : "text-red-400"}`} data-testid="fuel-score">
              {score}%
            </div>
          </div>
        </div>
      </div>

      {/* Daily */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[10px] mb-1">
          <span className="text-slate-500 uppercase tracking-widest">Today · Taken</span>
          <span className="font-mono-alt text-white" data-testid="fuel-daily-txt">{takenToday} / {dailyTarget}</span>
        </div>
        <div className="h-2 rounded-full bg-[#0A0E1E] border border-[#1E2742] overflow-hidden">
          <div className="h-full bg-[#FF8C00] transition-all duration-500" style={{ width: `${dailyPct}%` }} data-testid="fuel-daily-bar"/>
        </div>
      </div>
      {/* Monthly */}
      <div>
        <div className="flex items-center justify-between text-[10px] mb-1">
          <span className="text-slate-500 uppercase tracking-widest">Month · OK Cases</span>
          <span className="font-mono-alt text-white" data-testid="fuel-monthly-txt">{okThisMonth} / {monthlyTarget}</span>
        </div>
        <div className="h-2 rounded-full bg-[#0A0E1E] border border-[#1E2742] overflow-hidden">
          <div className="h-full bg-[#C5A028] transition-all duration-500" style={{ width: `${monthlyPct}%` }} data-testid="fuel-monthly-bar"/>
        </div>
      </div>
    </div>
  );
}
